import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrainerCard = ({ trainer }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleBookSession = () => {
    navigate('/class-schedule', { state: { trainerId: trainer?.id } });
  };

  const handleSendMessage = () => {
    // Mock message functionality
    alert(`Message sent to ${trainer?.name}`);
  };

  return (
    <div className="bg-card border border-border rounded-xl shadow-md overflow-hidden transition-smooth hover-lift">
      <div className="relative h-48 md:h-56 lg:h-64 overflow-hidden">
        <Image
          src={trainer?.image}
          alt={trainer?.imageAlt}
          className="w-full h-full object-cover"
        />
        {trainer?.isAvailable && (
          <div className="absolute top-4 right-4 flex items-center gap-2 bg-success text-success-foreground px-3 py-1.5 rounded-full shadow-md">
            <div className="w-2 h-2 bg-success-foreground rounded-full animate-pulse" />
            <span className="text-xs font-medium caption">Available</span>
          </div>
        )}
        {trainer?.featured && (
          <div className="absolute top-4 left-4 bg-accent text-accent-foreground px-3 py-1.5 rounded-full shadow-md">
            <span className="text-xs font-bold caption">Featured</span>
          </div>
        )}
      </div>
      <div className="p-4 md:p-5 lg:p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg md:text-xl font-heading font-bold text-foreground mb-1">
              {trainer?.name}
            </h3>
            <p className="text-sm text-muted-foreground caption">{trainer?.title}</p>
          </div>
          <div className="flex items-center gap-1 bg-warning/10 px-2 py-1 rounded-lg">
            <Icon name="Star" size={16} color="var(--color-warning)" fill="var(--color-warning)" />
            <span className="text-sm font-bold data-text text-foreground">{trainer?.rating}</span>
            <span className="text-xs text-muted-foreground caption">({trainer?.reviews})</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {trainer?.specializations?.slice(0, 3)?.map((spec, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium caption"
            >
              <Icon name="Award" size={12} />
              {spec}
            </span>
          ))}
          {trainer?.specializations?.length > 3 && (
            <span className="inline-flex items-center px-3 py-1 bg-muted text-muted-foreground rounded-full text-xs font-medium caption">
              +{trainer?.specializations?.length - 3} more
            </span>
          )}
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Icon name="Briefcase" size={16} color="var(--color-muted-foreground)" />
            <span className="caption">{trainer?.experience} years experience</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Icon name="Users" size={16} color="var(--color-muted-foreground)" />
            <span className="caption">{trainer?.clients} active clients</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Icon name="DollarSign" size={16} color="var(--color-muted-foreground)" />
            <span className="caption font-medium">${trainer?.hourlyRate}/hour</span>
          </div>
        </div>

        {isExpanded && (
          <div className="mb-4 p-4 bg-muted/50 rounded-lg space-y-3 transition-smooth">
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Certifications</h4>
              <div className="flex flex-wrap gap-2">
                {trainer?.certifications?.map((cert, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-card border border-border rounded text-xs caption"
                  >
                    <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
                    {cert}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Training Philosophy</h4>
              <p className="text-sm text-muted-foreground caption">{trainer?.philosophy}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Success Story</h4>
              <p className="text-sm text-muted-foreground caption italic">"{trainer?.successStory}"</p>
            </div>
          </div>
        )}

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-smooth mb-4"
        >
          <span className="caption">{isExpanded ? 'Show Less' : 'View Full Profile'}</span>
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
        </button>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="default"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={handleSendMessage}
            className="flex-1"
          >
            Message
          </Button>
          <Button
            variant="default"
            size="default"
            iconName="Calendar"
            iconPosition="left"
            onClick={handleBookSession}
            className="flex-1"
          >
            Book Session
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrainerCard;